// Chemin: C:\gestion-copro\src\services\documentAnalysis.service.ts
// Description: Service pour l'analyse IA de documents (Dropzone GED)
// Session 106 : Ajout categorie + categorieLabel dans DocumentSuggestion
//               Alignement sur taxonomie GED v2 retournée par la Cloud Function
// Session 111 : Ajout computeFileHash() — hash SHA-256 pour détection doublons
// Session 113 : Limite upload portée de 10 Mo à 25 Mo (alignement gardien.service.ts)
//               Les documents copro (DPE, PV AG, diagnostics) font souvent 10-20 Mo
// Session 132 : Ajout anomalies + donneesCles dans DocumentSuggestion
//               Alignement avec ce que le backend retourne depuis sessions 119-120
// Session 143 : Ajout sensCorrespondance dans DocumentSuggestion (entrant/sortant)
// Session 144 : Ajout champs Intelligence Correspondance dans DocumentSuggestion
//               (courrierId_lie, courrierRef_lie, typeCorrespondance, qualiteReponse,
//               pointsTraites, pointsManquants, alerteQualite)
// Session 145 : Ajout contenuRelanceSCUD + objetRelanceSCUD (IC Niveau 3)
// Session 196 : Support XML et XLSX — isFileAccepted() étendu + ACCEPTED_FILE_TYPES mis à jour
//               La conversion texte se fait côté Cloud Function (pas côté client)

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import app from './firebase';

// Initialisation des services Firebase
const functions = getFunctions(app, 'europe-west1');
const storage = getStorage(app);

// Types
export interface DocumentSuggestion {
  typeDocument: string;
  typeDocumentLabel: string;
  // Session 106 : Catégorie retournée directement par SENTINELLE
  categorie: string;
  categorieLabel: string;
  titre: string;
  description: string;
  dateDocument: string | null;
  interlocuteurId: string | null;
  interlocuteurNom: string | null;
  affaireId: string | null;
  affaireNom: string | null;
  confidence: 'haute' | 'moyenne' | 'basse';
  explication: string;
  // Session 119 : Anomalies détectées par SENTINELLE
  anomalies: string[];
  actionsRecommandees: string[];
  // Session 132 : Données clés structurées extraites par SENTINELLE
  donneesCles: Record<string, string>;
  // Session 143 : Sens de la correspondance (entrant/sortant) pour les emails et courriers
  sensCorrespondance?: 'entrant' | 'sortant' | null;
  // Session 144 : Intelligence Correspondance Niveau 1 — champs retournés par le backend
  courrierId_lie?: string | null;
  courrierRef_lie?: string | null;
  typeCorrespondance?: 'reponse' | 'relance' | 'accuseReception' | null;
  qualiteReponse?: 'complete' | 'partielle' | 'incomplete' | null;
  pointsTraites?: string[];
  pointsManquants?: string[];
  alerteQualite?: string | null;
  // Session 145 : Relance SCUD générée par SENTINELLE (IC Niveau 3)
  contenuRelanceSCUD?: string | null;
  objetRelanceSCUD?: string | null;
  // Session 216 : Niveau de relance calculé côté serveur (déterministe)
  // "Première relance" | "Première relance — délai dépassé" | "Deuxième relance" | "Mise en demeure imminente"
  niveauRelance?: string | null;
  // Session 244 : Identification item checklist documentaire
  checklistItemId?: string | null;
  // Session 244 : Qualité de lecture du document
  qualiteLecture?: 'bonne' | 'moyenne' | 'faible' | null;
}

export interface AnalyzeDocumentResponse {
  success: boolean;
  suggestion?: DocumentSuggestion;
  texteExtrait?: string;
  error?: string;
}

interface AnalyzeDocumentRequest {
  fileUrl: string;
  fileName: string;
  fileType: string;
  coproprieteId: string;
}

// Types de fichiers acceptés
export const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  // Session 196 : Formats structurés — conversion texte côté Cloud Function
  'text/xml': ['.xml'],
  'application/xml': ['.xml'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

export const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 Mo — Session 113 : aligné sur gardien.service.ts

/**
 * Vérifie si un fichier est accepté pour l'analyse
 */
export function isFileAccepted(file: File): { accepted: boolean; reason?: string } {
  // Vérifier le type
  const acceptedTypes = Object.keys(ACCEPTED_FILE_TYPES);
  if (!acceptedTypes.includes(file.type)) {
    return {
      accepted: false,
      reason: `Type de fichier non supporté: ${file.type}. Acceptés: PDF, JPEG, PNG, WebP, GIF, XML, XLSX.`,
    };
  }

  // Vérifier la taille
  if (file.size > MAX_FILE_SIZE) {
    const sizeMo = (file.size / (1024 * 1024)).toFixed(1);
    return {
      accepted: false,
      reason: `Fichier trop volumineux (${sizeMo} Mo). Maximum: 25 Mo.`,
    };
  }

  return { accepted: true };
}

// ============================================
// SESSION 111 : HASH SHA-256 — DÉTECTION DOUBLONS
// ============================================

/**
 * Calcule le hash SHA-256 d'un fichier côté client
 * Utilise l'API Web Crypto (native navigateur, aucune dépendance)
 * Retourne le hash en hexadécimal (64 caractères)
 *
 * Usage : avant l'archivage, comparer ce hash avec ceux stockés en Firestore
 * pour détecter les doublons exacts (même contenu binaire).
 */
export async function computeFileHash(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Upload un fichier vers le dossier temporaire
 * Retourne l'URL du fichier uploadé
 */
export async function uploadTempFile(
  file: File,
  coproprieteId: string,
  userId: string
): Promise<{ url: string; path: string }> {
  // Générer un nom unique
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filePath = `temp/${coproprieteId}/${userId}/${timestamp}_${safeName}`;

  const storageRef = ref(storage, filePath);

  // Upload le fichier
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  });

  // Récupérer l'URL
  const url = await getDownloadURL(storageRef);

  return { url, path: filePath };
}

/**
 * Supprime un fichier temporaire
 */
export async function deleteTempFile(filePath: string): Promise<void> {
  try {
    const storageRef = ref(storage, filePath);
    await deleteObject(storageRef);
  } catch (error) {
    // Ignorer les erreurs de suppression (fichier peut ne plus exister)
    console.warn('Erreur suppression fichier temp:', error);
  }
}

/**
 * Analyse un document via la Firebase Function
 */
export async function analyzeDocument(
  fileUrl: string,
  fileName: string,
  fileType: string,
  coproprieteId: string
): Promise<AnalyzeDocumentResponse> {
  const analyzeDocumentFn = httpsCallable<AnalyzeDocumentRequest, AnalyzeDocumentResponse>(
    functions,
    'analyzeDocument'
  );

  const result = await analyzeDocumentFn({
    fileUrl,
    fileName,
    fileType,
    coproprieteId,
  });

  return result.data;
}

/**
 * Formate la suggestion en message lisible pour le chat
 */
export function formatSuggestionAsMessage(
  suggestion: DocumentSuggestion,
  fileName: string
): string {
  const lines: string[] = [];

  lines.push(`## Analyse de "${fileName}"\n`);

  // Catégorie suggérée
  const confidenceEmoji =
    suggestion.confidence === 'haute' ? '🟢' :
    suggestion.confidence === 'moyenne' ? '🟡' : '🔴';

  lines.push(`**Type** : ${suggestion.typeDocumentLabel} ${confidenceEmoji}`);
  // Session 106 : Afficher aussi la catégorie
  lines.push(`**Catégorie** : ${suggestion.categorieLabel}`);
  lines.push(`**Titre proposé** : ${suggestion.titre}`);

  if (suggestion.description) {
    lines.push(`**Description** : ${suggestion.description}`);
  }

  // Date détectée
  if (suggestion.dateDocument) {
    lines.push(`**Date du document** : ${suggestion.dateDocument}`);
  }

  // Interlocuteur détecté
  if (suggestion.interlocuteurNom) {
    lines.push(`**Interlocuteur concerné** : ${suggestion.interlocuteurNom}`);
  }

  // Affaire liée
  if (suggestion.affaireNom) {
    lines.push(`**Affaire liée** : ${suggestion.affaireNom}`);
  }

  // Explication
  lines.push(`\n**Analyse** : ${suggestion.explication}`);

  // Actions recommandées
  if (suggestion.actionsRecommandees.length > 0) {
    lines.push(`\n**Actions recommandées** :`);
    suggestion.actionsRecommandees.forEach((action) => {
      lines.push(`- ${action}`);
    });
  }

  // Note sur l'archivage
  lines.push(`\n---`);
  lines.push(`*Utilisez le bouton "Archiver maintenant" ci-dessous pour enregistrer ce document dans la GED.*`);
  return lines.join('\n');
}